import React from 'react';
import PropTypes from 'prop-types';
import SimpleInfoPanel from '../SimpleInfoPanel';
import {Typography} from '@mui/material';
import {AccountType} from '../constants/securspace-constants';
import {formatPhoneNumber} from '../../util/ValidationUtils';
import BookSpaceInfoPanelContent from '../location/profile/BookSpaceInfoPanelContent';
import {formatCurrencyValue} from "../../util/PaymentUtils";
import {getLocationAllowsDailyBookings} from '../../util/AccountUtils';
import {Supplier} from "../../types/Supplier";
import {Account} from "../../interfaces";
import {styled} from "@mui/material/styles";

const SimpleAdminInfoPanel = styled(SimpleInfoPanel)(() => ({
  padding: '1.43rem',
  backgroundColor: '#FFF6D8',
  borderRadius: '0.57rem',
}));

const BaseContentTypography = styled(Typography)(({theme}) => ({
  marginTop: '0.86rem',
  color: theme.palette.common.black,
}));

const LOGGED_IN_USER_TYPE = Object.freeze({
  [AccountType.BUYER]: "Customer",
  [AccountType.SUPPLIER]: "Partner",
});

type AdminInfoPanelContentProps = {
  location: Supplier,
}

const AdminInfoPanelContent = ({ location }: AdminInfoPanelContentProps) => {

  const panelData = [
    {
      label: 'Distance',
      value: `${location?.distance} mi` || '',
    },
    {
      label: 'Partner Name',
      value: location?.companyName || '',
    },
    {
      label: 'Partner Email',
      value: location?.companyEmail || '',
    },
    {
      label: 'Partner Phone',
      value: formatPhoneNumber(location?.companyPhoneNumber) || '',
    },
    {
      label: 'Location Phone',
      value: formatPhoneNumber(location?.locationPhoneNumber) || '',
    },
    {
      label: 'Location Manager Name',
      value: location?.locationManagerName || '',
    },
    {
      label: 'Location Manager Phone',
      value: formatPhoneNumber(location?.locationManagerPhone) || '',
    },
    {
      label: 'Location Manager email',
      value: location?.locationManagerEmail || '',
    },
    {
      label: 'Max Spaces Booked',
      value: location?.maxSpacesBooked || '',
    },
    {
      label: 'Auto-Approve Spaces',
      value: location?.managedSpaces || '',
    },
    {
      label: 'Total Spaces',
      value: location?.totalNumberOfSpaces || '',
    },
    {
      label: 'Min Booking Spaces',
      value: location?.minNumberOfSpaces || '',
    },
    {
      label: 'Allow Daily Bookings',
      value: getLocationAllowsDailyBookings(location?.pricePerDay) ? "Yes" : "No",
    },
    {
      label: 'Overage Rate',
      value: formatCurrencyValue(location?.overageRate , true),
    },
    {
      label: 'Current Inventory',
      value: location?.currentInventoryCount || '0',
    },
    {
      label: 'Default Commission',
      value: location?.commissionRate || '',
    },
  ];


  return <BookSpaceInfoPanelContent data={panelData} />;
};

type AdminInfoPanelProps = {
  user: Account,
  location: Supplier,
}

const AdminInfoPanel = ({ user, location }: AdminInfoPanelProps) => {
  // This does the same thing more safely than the previous logic since AccountType.ADMIN doesn't have a key on LOGGED_IN_USER_TYPE
  const loggedInUserTitle = user.type !== AccountType.ADMIN ? `(${LOGGED_IN_USER_TYPE[user.type]}) ${user.companyName}` : user.companyName;

  return (
    <SimpleAdminInfoPanel
      title='Admin Info'
      content={<AdminInfoPanelContent location={location} />}
    >
      <BaseContentTypography
        variant='subtitle2'
      >
        CURRENTLY LOGGED IN AS: {loggedInUserTitle}
      </BaseContentTypography>
    </SimpleAdminInfoPanel>
  );
};

AdminInfoPanel.propTypes = {
  user: PropTypes.object.isRequired,
  location: PropTypes.object
};

export default AdminInfoPanel;
